import axios from 'axios'
import errorResponseHandler from './errorResponseHandler'
import { ENV } from '@forwardflow/helpers/configs'
import LocalStorage from '../LocalStorage'
import AuthService from '../AuthService'
import { CONNECT_ASSISTANT_PATH, peekHandle } from '../AssistantConnect'

let isRefreshing = false
let failedQueue: any[] = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  
  failedQueue = []
}

const API = axios.create({
  baseURL: ENV.PUBLIC_FF_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    // "X-Requested-With": "XMLHttpRequest",
  },
  withCredentials: true,
})

API.interceptors.request.use((config) => {
  const accessToken = LocalStorage?.get('access_token')
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }

  return config
})

API.interceptors.response.use(
  (response) => {
    // For ArrayBuffer responses (audio/video/binary data), return as-is
    if (response.data instanceof ArrayBuffer) {
      return {
        success: true,
        data: response.data,
      }
    }
    
    // For normal JSON responses
    const responseObject: any = {
      success: true,
      statusCode: response?.data?.statusCode,
      message: response?.data?.message,
      data: response?.data?.data,
      meta: response?.data?.meta,
    }
    return responseObject
  },
  async (err) => {
    const originalRequest = err.config

    // A refresh cannot join the queue waiting for that same refresh. Let its
    // caller reject the queue and clear the expired session instead.
    if (originalRequest?.url?.split('?')[0]?.endsWith('/auth/refresh-token')) {
      return Promise.reject(err)
    }
    
    if (err?.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return API(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const response = await AuthService.apiRefreshToken()
        const newAccessToken = response.accessToken
        
        LocalStorage.put('access_token', newAccessToken)
        API.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        
        processQueue(null, newAccessToken)
        return API(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        LocalStorage.delete('access_token')
        
        errorResponseHandler(refreshError)
        const handle = window.location.pathname === CONNECT_ASSISTANT_PATH
          ? peekHandle(window.location.search)
          : null
        window.location.href = handle
          ? `/signin?handle=${encodeURIComponent(handle)}`
          : '/signin'
        
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }
    
    errorResponseHandler(err)
    return Promise.reject(err)
  }
)

const LEAD_PROCESS_WORKER_API = axios.create({
  baseURL: ENV.LEAD_PROCESS_WORKER_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  },
  withCredentials: false,
})

LEAD_PROCESS_WORKER_API.interceptors.request.use((config) => {
  const accessToken = LocalStorage?.get('access_token')
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }

  return config
})

LEAD_PROCESS_WORKER_API.interceptors.response.use(
  (response) => {
    const responseObject: any = {
      success: true,
      statusCode: response?.data?.statusCode,
      message: response?.data?.message,
      data: response?.data?.data,
      meta: response?.data?.meta
    }
    return responseObject
  },
  (err) => {
    errorResponseHandler(err)
    return Promise.reject(err)
  }
)


const CAMPAIGN_WORKER_API = axios.create({
  baseURL: ENV.CAMPAIGN_UI_WORKER_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  },
  withCredentials: false,
})

CAMPAIGN_WORKER_API.interceptors.request.use((config) => {
  const accessToken = LocalStorage?.get('access_token')
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }

  return config
})

CAMPAIGN_WORKER_API.interceptors.response.use(
  (response) => {
    const responseObject: any = {
      success: true,
      statusCode: response?.data?.statusCode,
      message: response?.data?.message,
      data: response?.data?.data,
      meta: response?.data?.meta
    }
    return responseObject
  },
  (err) => {
    errorResponseHandler(err)
    return Promise.reject(err)
  }
)

const TEXT_WORKER_API = axios.create({
  baseURL: ENV.TEXT_WORKER_API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  },
  withCredentials: true
})

TEXT_WORKER_API.interceptors.request.use((config) => {
  const accessToken = LocalStorage?.get('access_token')
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }

  return config
})

TEXT_WORKER_API.interceptors.response.use(
  (response) => {
    const responseObject: any = {
      success: true,
      statusCode: response?.data?.statusCode,
      message: response?.data?.message,
      data: response?.data?.data,
      meta: response?.data?.meta
    }
    return responseObject
  },
  (err) => {
    errorResponseHandler(err)
    return Promise.reject(err)
  }
)

const PHONE_WORKER_API = axios.create({
  baseURL: ENV.PHONE_WORKER_API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  },
  withCredentials: true
})

PHONE_WORKER_API.interceptors.request.use((config) => {
  const accessToken = LocalStorage?.get('access_token')
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }

  return config
})

PHONE_WORKER_API.interceptors.response.use(
  (response) => {
    const responseObject: any = {
      success: true,
      statusCode: response?.data?.statusCode,
      message: response?.data?.message,
      data: response?.data?.data,
      meta: response?.data?.meta
    }
    return responseObject
  },
  (err) => {
    errorResponseHandler(err)
    return Promise.reject(err)
  }
)

const CALL_WORKER_API = axios.create({
  baseURL: ENV.CALL_WORKER_API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  },
  withCredentials: true
})

CALL_WORKER_API.interceptors.request.use((config) => {
  const accessToken = LocalStorage?.get('access_token')
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }

  return config
})

CALL_WORKER_API.interceptors.response.use(
  (response) => {
    const responseObject: any = {
      success: true,
      statusCode: response?.data?.statusCode,
      message: response?.data?.message,
      data: response?.data?.data,
      meta: response?.data?.meta
    }
    return responseObject
  },
  (err) => {
    errorResponseHandler(err)
    return Promise.reject(err)
  }
)

export { API, CAMPAIGN_WORKER_API, TEXT_WORKER_API, PHONE_WORKER_API, CALL_WORKER_API, LEAD_PROCESS_WORKER_API }
